import * as amplitude from '@amplitude/analytics-browser';

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    dataLayer?: any[];
  }
}

const amplitudeApiKey = import.meta.env.VITE_AMPLITUDE_API_KEY;
const ga4MeasurementId = import.meta.env.VITE_GA4_ID;

let isAmplitudeInitialized = false;
let isGa4Initialized = false;

function generateRandomId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
}

export function getSessionId(): string {
  if (typeof window === 'undefined') return 'server';
  try {
    let sess = sessionStorage.getItem('crisp_docs_session_id');
    if (!sess) {
      sess = 'sess_' + generateRandomId().slice(0, 16);
      sessionStorage.setItem('crisp_docs_session_id', sess);
    }
    return sess;
  } catch {
    return 'sess_fallback';
  }
}

export function getUserHash(): string {
  if (typeof window === 'undefined') return 'server';
  try {
    let uHash = localStorage.getItem('crisp_docs_user_hash');
    if (!uHash) {
      uHash = 'u_' + generateRandomId().slice(0, 16);
      localStorage.setItem('crisp_docs_user_hash', uHash);
    }
    return uHash;
  } catch {
    return 'u_anonymous';
  }
}

let eventQueue: any[] = [];
let flushTimeout: any = null;

export async function flushTelemetryQueue() {
  if (eventQueue.length === 0) return;
  const batch = [...eventQueue];
  eventQueue = [];

  try {
    await fetch('/api/v1/events', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(batch),
      keepalive: true,
    });
  } catch (err) {
    // Non-blocking telemetry
    console.debug('Telemetry flush deferred or failed:', err);
  }
}

export function sendTelemetryEvent(event: Record<string, any>) {
  if (typeof window === 'undefined') return;
  eventQueue.push({
    timestamp: new Date().toISOString(),
    session_id: getSessionId(),
    user_hash: getUserHash(),
    ...event,
  });

  if (!flushTimeout) {
    flushTimeout = setTimeout(() => {
      flushTimeout = null;
      flushTelemetryQueue();
    }, 250);
  }
}

/**
 * Initializes Amplitude Analytics if API key is provided.
 */
export function initAnalytics() {
  if (amplitudeApiKey && !isAmplitudeInitialized) {
    try {
      amplitude.init(amplitudeApiKey, undefined, {
        defaultTracking: {
          pageViews: false,
          sessions: true,
          formInteractions: false,
          fileDownloads: true,
        },
      });
      isAmplitudeInitialized = true;
    } catch (e) {
      console.warn('Amplitude initialization failed:', e);
    }
  }

  if (ga4MeasurementId && !isGa4Initialized && typeof window !== 'undefined') {
    try {
      if (!document.getElementById('ga4-script')) {
        const script = document.createElement('script');
        script.id = 'ga4-script';
        script.async = true;
        script.src = `https://www.googletagmanager.com/gtag/js?id=${ga4MeasurementId}`;
        document.head.appendChild(script);

        window.dataLayer = window.dataLayer || [];
        window.gtag = function () {
          window.dataLayer?.push(arguments);
        };
        window.gtag('js', new Date());
        window.gtag('config', ga4MeasurementId, { send_page_view: false });
      }
      isGa4Initialized = true;
    } catch (e) {
      console.warn('GA4 initialization failed:', e);
    }
  }
}

/**
 * Track page views across GA4, Amplitude, backend events, and parent embed window.
 */
export function trackPageView(slug: string, title?: string, properties?: Record<string, any>) {
  const isEmbed = Boolean(
    properties?.embed ||
    (typeof window !== 'undefined' && window.parent && window.parent !== window)
  );

  const payload = {
    path: typeof window !== 'undefined' ? window.location.pathname : '',
    slug,
    title: title || (typeof document !== 'undefined' ? document.title : slug),
    referrer: typeof document !== 'undefined' ? document.referrer : '',
    is_embed: isEmbed,
    ...properties,
  };

  // Amplitude
  if (isAmplitudeInitialized) {
    amplitude.track('Docs Page Viewed', payload);
  }

  // GA4
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'page_view', {
      page_title: payload.title,
      page_location: window.location.href,
      page_path: window.location.pathname,
      slug: payload.slug,
    });
  }

  // Backend /api/v1/events
  sendTelemetryEvent({
    type: 'page_view',
    slug: payload.slug,
    referrer: payload.referrer,
    is_embed: payload.is_embed,
  });

  // Embed telemetry
  notifyParentWindow('CRISP_DOCS_PAGE_VIEW', payload);
}

/**
 * Track search queries and interactions.
 */
export function trackSearch(
  query: string,
  resultCount: number,
  modeOrOptions?:
    | 'keyword'
    | 'ai'
    | 'hybrid'
    | {
        queryId?: string;
        latencyMs?: number;
        clickedRank?: number;
        clickedSlug?: string;
        mode?: 'keyword' | 'ai' | 'hybrid';
      }
) {
  const options =
    typeof modeOrOptions === 'string'
      ? { mode: modeOrOptions }
      : modeOrOptions;

  const payload = {
    query_text: query,
    query: query,
    query_id: options?.queryId || `q_${Date.now()}`,
    results_count: resultCount,
    latency_ms: options?.latencyMs,
    clicked_rank: options?.clickedRank,
    clicked_slug: options?.clickedSlug,
    search_mode: options?.mode || 'keyword',
  };

  if (isAmplitudeInitialized) {
    amplitude.track('Docs Search Performed', payload);
  }

  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'search', {
      search_term: query,
      result_count: resultCount,
      search_mode: payload.search_mode,
    });
  }

  // Backend /api/v1/events
  sendTelemetryEvent({
    type: 'search_query',
    ...payload,
  });

  notifyParentWindow('CRISP_DOCS_SEARCH', payload);
}

/**
 * Track feedback submissions ("Was this page helpful?").
 */
export function trackFeedback(data: {
  slug: string;
  helpful: boolean;
  category?: string;
  comment?: string;
}) {
  const vote = data.helpful ? 'up' : 'down';
  const tags = data.category ? [data.category] : [];

  const payload = {
    slug: data.slug,
    helpful: data.helpful,
    vote,
    category: data.category,
    tags,
    comment: data.comment,
  };

  if (isAmplitudeInitialized) {
    amplitude.track('Docs Feedback Submitted', payload);
  }

  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'docs_feedback', payload);
  }

  // Backend /api/v1/events
  sendTelemetryEvent({
    type: 'page_feedback',
    slug: data.slug,
    vote,
    tags,
    comment: data.comment,
  });

  notifyParentWindow('CRISP_DOCS_FEEDBACK', payload);
}

/**
 * Track AI answer rating.
 */
export function trackAiAnswerVote(data: {
  queryId: string;
  answerId: string;
  vote: 'up' | 'down';
}) {
  const payload = {
    query_id: data.queryId,
    answer_id: data.answerId,
    vote: data.vote,
  };

  if (isAmplitudeInitialized) {
    amplitude.track('Docs AI Answer Voted', payload);
  }

  sendTelemetryEvent({
    type: 'ai_answer_vote',
    ...payload,
  });
}

/**
 * In-product embed telemetry helper.
 * Posts messages to parent window when running inside an iframe.
 */
export function notifyParentWindow(type: string, data: Record<string, any>) {
  if (typeof window !== 'undefined' && window.parent && window.parent !== window) {
    try {
      window.parent.postMessage(
        {
          source: 'crisp-docs',
          type,
          data,
        },
        '*'
      );
    } catch (e) {
      console.warn('Failed to postMessage to parent:', e);
    }
  }
}
